import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';

const NotificationBanner = ({ className = "" }) => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [currentNotification, setCurrentNotification] = useState(null);
  const [isVisible, setIsVisible] = useState(false);

  const notificationTypes = {
    critical: {
      icon: 'AlertTriangle',
      bgColor: 'bg-error/10',
      borderColor: 'border-error',
      textColor: 'text-error',
      iconColor: 'text-error'
    },
    warning: {
      icon: 'AlertCircle',
      bgColor: 'bg-warning/10',
      borderColor: 'border-warning',
      textColor: 'text-warning',
      iconColor: 'text-warning'
    },
    info: {
      icon: 'Info',
      bgColor: 'bg-accent/10',
      borderColor: 'border-accent',
      textColor: 'text-accent',
      iconColor: 'text-accent'
    },
    success: {
      icon: 'CheckCircle',
      bgColor: 'bg-success/10',
      borderColor: 'border-success',
      textColor: 'text-success',
      iconColor: 'text-success'
    }
  };

  const sampleNotifications = [
    {
      id: 1,
      type: 'critical',
      title: 'Unauthorized Access Attempt',
      message: 'Multiple failed login attempts detected from IP 192.168.1.100',
      timestamp: new Date(),
      action: 'View Details',
      actionPath: '/alerts-notifications'
    },
    {
      id: 2,
      type: 'warning',
      title: 'System Maintenance Required',
      message: 'Security certificate expires in 7 days',
      timestamp: new Date(Date.now() - 300000),
      action: 'Schedule Maintenance',
      actionPath: '/settings'
    },
    {
      id: 3,
      type: 'info',
      title: 'New Device Registered',
      message: 'Smart lock SL-001 successfully added to network',
      timestamp: new Date(Date.now() - 600000),
      action: 'View Device',
      actionPath: '/dashboard'
    },
    {
      id: 4,
      type: 'success',
      title: 'Backup Completed',
      message: 'Daily security logs backup completed successfully',
      timestamp: new Date(Date.now() - 900000),
      action: 'View Logs',
      actionPath: '/access-logs'
    }
  ];

  useEffect(() => {
    // Simulate real-time notifications
    const notificationInterval = setInterval(() => {
      const randomNotification = sampleNotifications?.[Math.floor(Math.random() * sampleNotifications?.length)];
      const newNotification = {
        ...randomNotification,
        id: Date.now(),
        timestamp: new Date()
      };
      
      setNotifications(prev => [newNotification, ...prev?.slice(0, 4)]);
      setCurrentNotification(newNotification);
      setIsVisible(true);

      // Auto-hide after 8 seconds for non-critical notifications
      if (newNotification?.type !== 'critical') {
        setTimeout(() => {
          setIsVisible(false);
        }, 8000);
      }
    }, 20000);

    return () => clearInterval(notificationInterval);
  }, []);

  const handleDismiss = () => {
    setIsVisible(false);
    setTimeout(() => {
      setCurrentNotification(null);
    }, 300);
  };

  const handleAction = (actionPath) => {
    navigate(actionPath);
    handleDismiss();
  };

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / 60000);
    
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };

  if (!currentNotification || !isVisible) {
    return null;
  }

  const typeConfig = notificationTypes?.[currentNotification?.type];

  return (
    <div className={`fixed top-16 left-4 right-4 z-40 security-transition ${className}`}>
      <div className={`
        ${typeConfig?.bgColor} ${typeConfig?.borderColor} 
        border-l-4 rounded-lg security-shadow-card glass-morphism
        transform transition-all duration-300 ease-out
        ${isVisible ? 'translate-y-0 opacity-100' : '-translate-y-full opacity-0'}
      `}>
        <div className="flex items-start space-x-3 p-4">
          {/* Icon */}
          <div className={`flex-shrink-0 ${currentNotification?.type === 'critical' ? 'security-pulse' : ''}`}>
            <Icon 
              name={typeConfig?.icon} 
              size={20} 
              className={typeConfig?.iconColor}
            />
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h4 className={`text-sm font-semibold ${typeConfig?.textColor}`}>
                  {currentNotification?.title}
                </h4>
                <p className="text-sm text-foreground mt-1">
                  {currentNotification?.message}
                </p>
                <p className="text-xs text-muted-foreground mt-2">
                  {formatTimeAgo(currentNotification?.timestamp)}
                </p>
              </div>

              {/* Actions */}
              <div className="flex items-center space-x-2 ml-4">
                {currentNotification?.action && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleAction(currentNotification?.actionPath)}
                    className={`${typeConfig?.textColor} hover:bg-current/10 text-xs`}
                  >
                    {currentNotification?.action}
                  </Button>
                )}
                
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleDismiss}
                  iconName="X"
                  className="text-muted-foreground hover:text-foreground p-1"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Progress bar for auto-dismiss */}
        {currentNotification?.type !== 'critical' && (
          <div className="h-1 bg-border/20 rounded-b-lg overflow-hidden">
            <div 
              className={`h-full ${typeConfig?.borderColor?.replace('border-', 'bg-')} animate-pulse`}
              style={{
                animation: 'progress-bar 8s linear forwards'
              }}
            />
          </div>
        )}
      </div>
      <style jsx>{`
        @keyframes progress-bar {
          from { width: 100%; }
          to { width: 0%; }
        }
      `}</style>
    </div>
  );
};

export default NotificationBanner;